import { ListCountEstimateMode, ListCountRequestMode } from 'notable-common';
import { z } from 'zod';
import { FlowRunState } from './flowRunState.js';
import { FlowRunType } from './runType.js';
export const FLOW_RUN_LIST_DEFAULT_LIMIT = 50;
export const FLOW_RUN_LIST_MAX_LIMIT = 10_000;
const EntityIdSchema = z.uuid().describe('Entity identifier; provide entityType with it.');
const EntityTypeSchema = z
    .string()
    .min(1)
    .describe('Entity kind paired with entityId, such as encounter or ehrInformation.');
const FlowIdSchema = z.uuid().describe('Flow identifier across all of its definition versions.');
const FlowDefinitionIdSchema = z
    .uuid()
    .describe('Saved definition version identifier; use when one version matters.');
const FlowRunListFiltersSchema = z.object({
    state: z.enum(FlowRunState).describe('Limit results to this execution state.').optional(),
    type: z.enum(FlowRunType).describe('Limit results to production or test runs.').optional(),
    createdAfter: z.iso
        .datetime({ offset: true })
        .describe('Inclusive ISO-8601 lower bound. Prefer about 90 days unless more history is needed; omitting it searches all available history.')
        .optional(),
    createdBefore: z.iso
        .datetime({ offset: true })
        .describe('Return runs created before this ISO-8601 timestamp, including an offset.')
        .optional(),
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(FLOW_RUN_LIST_MAX_LIMIT)
        .describe(`Defaults to ${FLOW_RUN_LIST_DEFAULT_LIMIT.toString()}. Use larger pages, up to ${FLOW_RUN_LIST_MAX_LIMIT.toString()}, for bounded metadata aggregation.`)
        .optional()
        .default(FLOW_RUN_LIST_DEFAULT_LIMIT),
    cursor: z
        .uuid()
        .describe('Exclusive paging cursor returned as nextCursor by the previous page.')
        .optional(),
    countMode: z
        .enum(ListCountRequestMode)
        .describe('None omits the count. Exact scans every match. Estimated scans at most 4,097 matches and may return a capped lower estimate when more than 4,096 match.')
        .optional()
        .default(ListCountRequestMode.None),
});
export const FlowRunListQuerySchema = z
    .union([
    FlowRunListFiltersSchema.extend({
        entityId: EntityIdSchema,
        entityType: EntityTypeSchema,
        flowId: FlowIdSchema.optional(),
        flowDefinitionId: FlowDefinitionIdSchema.optional(),
    }).strict(),
    FlowRunListFiltersSchema.extend({
        flowId: FlowIdSchema,
        flowDefinitionId: FlowDefinitionIdSchema.optional(),
    }).strict(),
    FlowRunListFiltersSchema.extend({
        flowDefinitionId: FlowDefinitionIdSchema,
    }).strict(),
])
    .describe('Select runs with one or more identifiers. Prefer a 90-day window and combine identifiers, state, or type to narrow high-volume searches; omit createdAfter only when all available history is needed.');
export const FlowRunListItemSchema = z.object({
    id: z.uuid().describe('Unique flow run identifier.'),
    flowDefinitionId: z.uuid().describe('Definition version executed by this run.'),
    flowId: z.uuid().describe('Flow executed by this run.'),
    flowName: z.string().describe('Name of the flow executed by this run.'),
    agentId: z.uuid().nullable().describe('Agent containing the flow, when assigned.'),
    agentName: z
        .string()
        .nullable()
        .describe('Name of the agent containing the flow, when assigned.'),
    state: z.enum(FlowRunState).describe('Current execution state of the run.'),
    type: z.enum(FlowRunType).describe('Whether this is a production or test run.'),
    created: z.iso.datetime({ offset: true }).describe('When the run was created.'),
    startedAt: z.iso
        .datetime({ offset: true })
        .nullable()
        .describe('When execution started, or null if not started.'),
    endedAt: z.iso
        .datetime({ offset: true })
        .nullable()
        .describe('When execution ended, or null if not ended.'),
});
export const FlowRunListResponseSchema = z
    .object({
    flowRuns: z.array(FlowRunListItemSchema).describe('Run summaries on this page.'),
    paginationSummary: z
        .object({
        total: z.number().int().nonnegative(),
        status: z.enum(ListCountEstimateMode),
    })
        .describe('Match count and its accuracy status. When status is Estimated, total is a capped lower estimate.')
        .optional(),
    limit: z.number().int().positive().max(FLOW_RUN_LIST_MAX_LIMIT),
    nextCursor: z
        .uuid()
        .nullable()
        .describe('Cursor for the next page, or null when this is the final page.'),
})
    .describe('A page of minimal flow run summaries matching selective filters.');
//# sourceMappingURL=list.js.map